import React, { useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Clickable from 'components/Common/Clickable/Clickable'
import Expander from 'components/utils/Expander'
import LoyaltyAccountTileCircularProgressBar from 'loyalty/components/LoyaltyAccountTileCircularProgressBar'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { getTierThreshold } from 'loyalty/selectors/getLoyaltyTierThresholds'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineMinusIcon from 'LuxKit/Icons/line/LineMinusIcon'
import TextLink from 'LuxKit/TextLink'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Tooltip from 'LuxKit/Tooltip'
import TierProgressionBenefitTitle, { BENEFIT_ICONS } from './TierProgressionBenefitTitle'

const TierTableContainer = styled.div`
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;

  &.tier-bronze {
    border: 1px solid ${props => props.theme.palette.product.loyalty.bronze.normalBackground};
  }

  &.tier-silver {
    border: 1px solid ${props => props.theme.palette.product.loyalty.silver.normalBackground};
  }

  &.tier-gold {
    border: 1px solid ${props => props.theme.palette.product.loyalty.gold.normalBackground};
  }

  &.tier-platinum {
    border: 1px solid ${props => props.theme.palette.product.loyalty.platinum.normalBackground};
  }
`

const HowToUnlockHeaderBase = css`
  width: 100%;
  padding: ${rem(12)} ${rem(16)};
  display: flex;
  justify-content: space-between;
  align-items: center;

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }

  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }

  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }

  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

const HowToUnlockHeader = styled(Clickable)`
  ${HowToUnlockHeaderBase}
`

const HowToUnlockHeaderStatic = styled.div`
  ${HowToUnlockHeaderBase}
`

const TierTableContent = styled.div`
  padding: ${rem(16)};
`

const TierDivider = styled(Divider)`
  opacity: 0.5;

  &.tier-bronze {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }

  &.tier-silver {
    background-color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }

  &.tier-gold {
    background-color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }

  &.tier-platinum {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.lightContrast};
  }
`

const ProgressSection = styled.div`
  display: flex;
  justify-content: center;
  gap: ${rem(16)};
  padding-top: ${rem(16)};
`

const ProgressItem = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(12)};
  text-align: center;

  @media (min-width: 576px) {
    flex-direction: row;
    text-align: left;
  }
`

const ProgressText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const BenefitsSection = styled.div`
  border-radius: ${props => props.theme.borderRadius.M};
`

const BenefitRow = styled(Group)`
  padding: ${rem(8)} 0;
`

const IconBadge = styled.div`
  border-radius: ${props => props.theme.borderRadius.round};
  width: ${rem(24)};
  height: ${rem(24)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &.tier-bronze {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.darkContrast};
  }

  &.tier-silver {
    background-color: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
  }

  &.tier-gold {
    background-color: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
  }

  &.tier-platinum {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const INITIAL_BENEFITS_COUNT = 6

interface Props {
  tier: App.LoyaltyTier
  tierThresholds: Record<App.LoyaltyTier, App.LoyaltyTierThreshold>
  currentStatusCredits: number
  currentNights: number
  statusReviewDate: string | undefined
  pendingPoints: { points: number; statusCredits: number; nights: number }
  tierBenefits: Array<App.LoyaltyRegionTierBenefit>
  isExpanded: boolean
  isSingleTier: boolean
  onToggle: (tier: App.LoyaltyTier) => void
  pendingCreditsTooltip: string
  pendingNightsTooltip: string
  showLessText: string
  showMoreText: string
  checkoutDate?: string
}

function TierProgressionAccordionItem({
  tier,
  tierThresholds,
  currentStatusCredits,
  currentNights,
  statusReviewDate,
  pendingPoints,
  tierBenefits,
  isExpanded,
  isSingleTier,
  onToggle,
  pendingCreditsTooltip,
  pendingNightsTooltip,
  showLessText,
  showMoreText,
  checkoutDate,
}: Props) {
  const tierName = getLoyaltyTierDisplayName(tier)
  const tierRequirement = getTierThreshold(tierThresholds, tier)

  const [showAllBenefits, setShowAllBenefits] = useState(false)
  const toggleShowAllBenefits = useCallback(() => {
    setShowAllBenefits(prev => !prev)
  }, [])

  const creditsProgress = Math.min((currentStatusCredits / tierRequirement.statusCredits) * 100, 100)
  const nightsProgress = Math.min((currentNights / tierRequirement.nights) * 100, 100)
  const statusCreditsNeeded = Math.max(tierRequirement.statusCredits - currentStatusCredits, 0)
  const nightsNeeded = Math.max(tierRequirement.nights - currentNights, 0)
  const hasDate = !!statusReviewDate

  const hasMoreBenefits = tierBenefits.length > INITIAL_BENEFITS_COUNT
  const displayedBenefits = showAllBenefits ? tierBenefits : tierBenefits.slice(0, INITIAL_BENEFITS_COUNT)

  const handleToggle = useCallback(() => {
    onToggle(tier)
  }, [onToggle, tier])

  const boldFormatter = useCallback((chunks: React.ReactNode) => <b>{chunks}</b>, [])

  const bookPackageMessage = useMemo(() => {
    if (statusCreditsNeeded > 0 && nightsNeeded > 0 && hasDate) {
      return (
        <FormattedMessage
          id="b7TGHi0ZB4"
          defaultMessage="Book a package worth <b>{statusCredits} Status Credits</b> and <b>{nights} Nights</b> and complete your trip by <b>{date}</b> to unlock {tierName} status."
          description="Modal message with credits, nights, and review date"
          values={{
            statusCredits: statusCreditsNeeded,
            nights: nightsNeeded,
            date: <FormatDateTime value={statusReviewDate} day="numeric" month="short" year="numeric" />,
            tierName,
            b: boldFormatter,
          }}
        />
      )
    }
    if (statusCreditsNeeded > 0 && nightsNeeded > 0) {
      return (
        <FormattedMessage
          id="hxzjRc6Ri6"
          defaultMessage="Book a package worth <b>{statusCredits} Status Credits</b> and <b>{nights} Nights</b> to unlock {tierName} status."
          description="Modal message with credits and nights in the loyalty program section"
          values={{
            statusCredits: statusCreditsNeeded,
            nights: nightsNeeded,
            tierName,
            b: boldFormatter,
          }}
        />
      )
    }
    if (statusCreditsNeeded > 0 && hasDate) {
      return (
        <FormattedMessage
          id="1OhpScV4on"
          defaultMessage="Book a package worth <b>{statusCredits} Status Credits</b> and complete your trip by <b>{date}</b> to unlock {tierName} status."
          description="Modal message with credits and review date in the loyalty section"
          values={{
            statusCredits: statusCreditsNeeded,
            date: <FormatDateTime value={statusReviewDate} day="numeric" month="short" year="numeric" />,
            tierName,
            b: boldFormatter,
          }}
        />
      )
    }
    if (statusCreditsNeeded > 0) {
      return (
        <FormattedMessage
          id="FbU+K7nc6d"
          defaultMessage="Book a package worth <b>{statusCredits} Status Credits</b> to unlock {tierName} status."
          description="Modal message with credits only in the loyalty program section"
          values={{
            statusCredits: statusCreditsNeeded,
            tierName,
            b: boldFormatter,
          }}
        />
      )
    }
    if (nightsNeeded > 0 && hasDate) {
      return (
        <FormattedMessage
          id="G93KVRkjG0"
          defaultMessage="Book a package worth <b>{nights} Nights</b> and complete your trip by <b>{date}</b> to unlock {tierName} status."
          description="Modal message with nights and review date in the loyalty section"
          values={{
            nights: nightsNeeded,
            date: <FormatDateTime value={statusReviewDate} day="numeric" month="short" year="numeric" />,
            tierName,
            b: boldFormatter,
          }}
        />
      )
    }
    return (
      <FormattedMessage
        id="T1j9ZtLQD0"
        defaultMessage="Book a package worth <b>{nights} Nights</b> to unlock {tierName} status."
        description="Modal message with nights only in the loyalty program section"
        values={{
          nights: nightsNeeded,
          tierName,
          b: boldFormatter,
        }}
      />
    )
  }, [statusCreditsNeeded, nightsNeeded, hasDate, statusReviewDate, tierName, boldFormatter])

  const header = useMemo(() => {
    const headerContent = (
      <BodyText variant="medium" weight="bold">
        <FormattedMessage
          id="U/oH6r4vSt"
          defaultMessage="How to unlock {tierName}"
          description="Header for how to unlock tier section in the loyalty program section"
          values={{ tierName }}
        />
      </BodyText>
    )

    if (isSingleTier) {
      return <HowToUnlockHeaderStatic className={`tier-${tier}`}>{headerContent}</HowToUnlockHeaderStatic>
    }

    const toggleIcon = isExpanded ? <LineMinusIcon size={20} /> : <LinePlusIcon size={20} />
    return (
      <HowToUnlockHeader className={`tier-${tier}`} onClick={handleToggle}>
        {headerContent}
        {toggleIcon}
      </HowToUnlockHeader>
    )
  }, [tier, tierName, isSingleTier, isExpanded, handleToggle])

  return (
    <VerticalSpacer gap={16}>
      <TierTableContainer className={`tier-${tier}`}>
        {header}
        <Expander expanded={isExpanded}>
          <TierTableContent>
            <BodyText variant="medium" colour="neutral-two">
              {bookPackageMessage}
            </BodyText>

            <ProgressSection>
              <ProgressItem>
                <LoyaltyAccountTileCircularProgressBar variant="light" progress={creditsProgress} tier={tier}>
                  <Group direction="vertical" horizontalAlign="center" gap={2}>
                    <BodyText variant="medium" weight="bold">
                      <FormatDecimal value={currentStatusCredits} />
                    </BodyText>
                    <TierDivider className={`tier-${tier}`} kind="primary" />
                    <Caption variant="small">
                      <FormatDecimal value={tierRequirement.statusCredits} />
                    </Caption>
                  </Group>
                </LoyaltyAccountTileCircularProgressBar>
                <ProgressText>
                  <BodyText variant="small" weight="bold">
                    <FormattedMessage
                      id="Yt/xPx+tgP"
                      defaultMessage="Status Credits"
                      description="Form field label for status credits progress in the loyalty program section"
                    />
                  </BodyText>
                  <BodyTextBlock
                    variant="small"
                    colour="neutral-two"
                    endIcon={<Tooltip description={pendingCreditsTooltip} placement="bottom"><LineInfoCircleIcon /></Tooltip>}
                  >
                    <FormattedMessage
                      id="5HNMOZQUqP"
                      defaultMessage="{credits} pending"
                      description="Pending status credits display in the loyalty program section"
                      values={{ credits: <FormatDecimal value={pendingPoints.statusCredits} /> }}
                    />
                  </BodyTextBlock>
                </ProgressText>
              </ProgressItem>

              <ProgressItem>
                <LoyaltyAccountTileCircularProgressBar variant="light" progress={nightsProgress} tier={tier}>
                  <Group direction="vertical" horizontalAlign="center" gap={2}>
                    <BodyText variant="medium" weight="bold">
                      {currentNights}
                    </BodyText>
                    <TierDivider className={`tier-${tier}`} kind="primary" />
                    <Caption variant="small">
                      {tierRequirement.nights}
                    </Caption>
                  </Group>
                </LoyaltyAccountTileCircularProgressBar>
                <ProgressText>
                  <BodyText variant="small" weight="bold">
                    <FormattedMessage
                      id="HtxgGJe0Jn"
                      defaultMessage="{count, plural, one {Night} other {Nights}}"
                      description="Pluralised label for the nights progress indicator in the loyalty tier tracker"
                      values={{ count: tierRequirement.nights }}
                    />
                  </BodyText>
                  <BodyTextBlock
                    variant="small"
                    colour="neutral-two"
                    endIcon={<Tooltip description={pendingNightsTooltip} placement="bottom"><LineInfoCircleIcon /></Tooltip>}
                  >
                    <FormattedMessage
                      id="w7O8IE7ByL"
                      defaultMessage="{nights} pending"
                      description="Display text showing the number of pending nights awaiting loyalty program credit"
                      values={{ nights: pendingPoints.nights }}
                    />
                  </BodyTextBlock>
                </ProgressText>
              </ProgressItem>
            </ProgressSection>
          </TierTableContent>
        </Expander>
      </TierTableContainer>

      <Expander expanded={isExpanded}>
        <VerticalSpacer gap={16}>
          {tierBenefits.length > 0 && (
            <BenefitsSection>
              <VerticalSpacer gap={8}>
                <BodyTextBlock variant="large" weight="bold">
                  <FormattedMessage
                    id="R9Qd3DAoPP"
                    defaultMessage="Benefits of {tierName}"
                    description="Header for tier benefits section in the loyalty program section"
                    values={{ tierName }}
                  />
                </BodyTextBlock>
                <div>
                  {displayedBenefits.map(benefit => (
                    <BenefitRow key={benefit.code} direction="horizontal" gap={12} verticalAlign="center">
                      <IconBadge className={`tier-${tier}`}>
                        {BENEFIT_ICONS[benefit.code]}
                      </IconBadge>
                      <BodyText variant="medium"><TierProgressionBenefitTitle benefit={benefit} /></BodyText>
                    </BenefitRow>
                  ))}
                </div>
                {hasMoreBenefits && (
                  <TextLink size="medium" onClick={toggleShowAllBenefits}>
                    {showAllBenefits ? showLessText : showMoreText}
                  </TextLink>
                )}
              </VerticalSpacer>
            </BenefitsSection>
          )}

          <VerticalSpacer gap={12}>
            <BodyTextBlock variant="large" weight="bold">
              <FormattedMessage
                id="1DzWhI6tXQ"
                defaultMessage="When you’ll receive {tierName} benefits"
                description="Header for when benefits activate section in the loyalty section"
                values={{ tierName }}
              />
            </BodyTextBlock>
            <BodyText variant="medium" colour="neutral-two">
              {checkoutDate && <FormattedMessage
                id="BeEbcUFwoD"
                defaultMessage="If you qualify, your {tierName} benefits will activate on {date} (the day you return from your tier-qualifying trip)."
                description="Message about when benefits activate with checkout date"
                values={{
                  tierName,
                  date: <FormatDateTime value={checkoutDate} day="numeric" month="numeric" year="numeric" />,
                }}
              />}
              {!checkoutDate && <FormattedMessage
                id="roZ1uKBh3A"
                defaultMessage="If you qualify, your {tierName} benefits will activate the day you return from your tier-qualifying trip."
                description="Message about when benefits activate without date in the loyalty program section"
                values={{
                  tierName,
                }}
              />}
            </BodyText>
          </VerticalSpacer>

          <VerticalSpacer gap={12}>
            <BodyTextBlock variant="large" weight="bold">
              <FormattedMessage
                id="cUAbL9g0u4"
                defaultMessage="How your current trips count"
                description="Header for how trips count section in the trip planner feature"
              />
            </BodyTextBlock>
            <BodyText variant="medium" colour="neutral-two">
              <FormattedMessage
                id="N7BaxsclF7"
                defaultMessage="Any upcoming trips already in your account will count toward your progress once you complete them."
                description="Description of how trips count toward progress in the loyalty program section"
              />
            </BodyText>
            <ul>
              <li>
                <BodyText variant="medium" colour="neutral-two">
                  <FormattedMessage
                    id="8xFwtbI6Os"
                    defaultMessage="Trips booked but not yet completed don’t contribute until your stay is finished."
                    description="Informational note explaining that booked but incomplete trips do not contribute to loyalty progress"
                  />
                </BodyText>
              </li>
              <li>
                <BodyText variant="medium" colour="neutral-two">
                  <FormattedMessage
                    id="ualkjjKSSE"
                    defaultMessage="We’ll automatically update your Status Credits and Nights after each completed trip."
                    description="Informational note explaining that status credits and nights are automatically updated after trips"
                  />
                </BodyText>
              </li>
            </ul>
          </VerticalSpacer>
        </VerticalSpacer>
      </Expander>
    </VerticalSpacer>
  )
}

export default TierProgressionAccordionItem
